export enum ConditionType {
  HARDCODE = 'hardcode',
  LOOKUP_MAP = 'lookup_map',
  GET_MAX_DATE = 'get_max_date',
  DIRECT_MAPPING = 'direct_mapping',
  FIRST_AVAILABLE = 'first_available',
  FIRST_AVAILABLE_MAP = 'first_available_map',
  CONTAINS_MAP = 'contains_map',
  MULTIPLY = 'multiply',
  CONDITIONAL_NUMERIC = 'conditional_numeric',
  CONCAT = 'concat',
}

export const CONCAT_MIN_SOURCES = 2
export const CONCAT_MAX_SOURCES = 5

export enum NumericComparisonOperator {
  EQUAL = 'equal',
  LESS_THAN = 'less_than',
  LESS_THAN_OR_EQUAL = 'less_than_or_equal',
  MORE_THAN = 'more_than',
  MORE_THAN_OR_EQUAL = 'more_than_or_equal',
}

export enum NormalizeType {
  TRIM = 'trim',
  UPPERCASE = 'uppercase',
  LOWERCASE = 'lowercase',
  REMOVE_SPECIAL_CHARS = 'remove_special_chars',
  SUBSTRING = 'substring',
  PAD_LEFT = 'pad_left',
  PAD_RIGHT = 'pad_right',
  DATE_FORMAT = 'date_format',
  MAX_LENGTH = 'max_length',
  REPLACE = 'replace',
  SLICE_LEFT = 'slice_left',
  SLICE_RIGHT = 'slice_right',
}

export interface TrimStep {
  type: NormalizeType.TRIM
}

export interface UppercaseStep {
  type: NormalizeType.UPPERCASE
}

export interface LowercaseStep {
  type: NormalizeType.LOWERCASE
}

export interface RemoveSpecialCharsStep {
  type: NormalizeType.REMOVE_SPECIAL_CHARS
  chars: string[]
}

export interface SubstringStep {
  type: NormalizeType.SUBSTRING
  start: number
  length: number
}

export interface PadLeftStep {
  type: NormalizeType.PAD_LEFT
  length: number
  pad_char: string
}

export interface PadRightStep {
  type: NormalizeType.PAD_RIGHT
  length: number
  pad_char: string
}

export interface DateFormatStep {
  type: NormalizeType.DATE_FORMAT
  format: string
}

export interface MaxLengthStep {
  type: NormalizeType.MAX_LENGTH
  length: number
}

export interface ReplaceStep {
  type: NormalizeType.REPLACE
  pattern: string
  replacement: string
  case_insensitive?: boolean
}

export interface SliceLeftStep {
  type: NormalizeType.SLICE_LEFT
  length: number
}

export interface SliceRightStep {
  type: NormalizeType.SLICE_RIGHT
  length: number
}

export type NormalizeStep =
  | TrimStep
  | UppercaseStep
  | LowercaseStep
  | RemoveSpecialCharsStep
  | SubstringStep
  | PadLeftStep
  | PadRightStep
  | DateFormatStep
  | MaxLengthStep
  | ReplaceStep
  | SliceLeftStep
  | SliceRightStep

export interface HardcodeRule {
  target: string
  condition: ConditionType.HARDCODE
  value: string
}

export interface LookupMapSingleSourceRule {
  target: string
  condition: ConditionType.LOOKUP_MAP
  source: string
  mapping: Record<string, string>
  default?: string
}

export interface LookupMapMultiSourceRule {
  target: string
  condition: ConditionType.LOOKUP_MAP
  sources: string[]
  mapping: Record<string, unknown>
  default?: string
}

export type LookupMapRule = LookupMapSingleSourceRule | LookupMapMultiSourceRule

export interface GetMaxDateRule {
  target: string
  condition: ConditionType.GET_MAX_DATE
  sources: string[]
}

export interface DirectMappingRule {
  target: string
  condition: ConditionType.DIRECT_MAPPING
  source: string
  normalize?: NormalizeStep[]
}

export interface FirstAvailableRule {
  target: string
  condition: ConditionType.FIRST_AVAILABLE
  sources: string[]
  normalize?: NormalizeStep[]
}

export interface FirstAvailableSourceEntry {
  source: string
  value: string
}

export interface FirstAvailableMapRule {
  target: string
  condition: ConditionType.FIRST_AVAILABLE_MAP
  sources: FirstAvailableSourceEntry[]
  default?: string
}

export interface ContainsMapRule {
  target: string
  condition: ConditionType.CONTAINS_MAP
  source: string
  mapping: Record<string, string>
  default?: string
  case_insensitive?: boolean
}

export interface MultiplyRule {
  target: string
  condition: ConditionType.MULTIPLY
  source: string
  constant: number
}

export interface ConditionalNumericRuleCondition {
  operator: NumericComparisonOperator
  compareValue: number | string
  outputValue: string
}

export interface ConditionalNumericRule {
  target: string
  condition: ConditionType.CONDITIONAL_NUMERIC
  sourceField: string
  rules: ConditionalNumericRuleCondition[]
}

export interface ConcatRule {
  target: string
  condition: ConditionType.CONCAT
  /** Ordered list of source columns joined together. Must have 2-5 entries. */
  sources: string[]
  /** String inserted between sources; may be empty ("") for a plain join. */
  delimiter: string
}

export type BackendMappingRule =
  | HardcodeRule
  | LookupMapRule
  | GetMaxDateRule
  | DirectMappingRule
  | FirstAvailableRule
  | FirstAvailableMapRule
  | ContainsMapRule
  | MultiplyRule
  | ConditionalNumericRule
  | ConcatRule

export interface MappingRulesConfig {
  schema_version: string
  computed_fields?: BackendMappingRule[]
  mapping_rules: BackendMappingRule[]
}

/** @deprecated Use FirstAvailableSourceEntry instead */
export type SourceEntry = FirstAvailableSourceEntry

/**
 * UI-friendly MappingRule with all fields optional for form state management.
 * The transformer casts this to BackendMappingRule types internally.
 */
export interface MappingRule {
  id: string
  target: string
  condition: ConditionType
  /** hardcode */
  value?: string
  /** direct_mapping, lookup_map (single source), contains_map, multiply */
  source?: string
  /** get_max_date, first_available, lookup_map (multiple sources) */
  sources?: string[]
  /** first_available_map */
  sourceEntries?: SourceEntry[]
  /** lookup_map */
  mapping?: Record<string, unknown>
  /** lookup_map, first_available_map, contains_map default fallback */
  default?: string
  /** direct_mapping, first_available */
  normalize?: NormalizeStep[]
  /** contains_map */
  case_insensitive?: boolean
  /** multiply: numeric multiplier applied to source */
  constant?: number
  /** conditional_numeric: selected numeric source column */
  sourceField?: string
  /** conditional_numeric: ordered first-match comparison rules */
  rules?: ConditionalNumericRuleCondition[]
  /** concat: string inserted between joined sources (may be empty) */
  delimiter?: string
}

export interface ParsedFile {
  columns: string[]
  rows: Record<string, string>[]
  fileName: string
  recordCount: number
  columnCount: number
}

export interface MappingRulesSchema {
  schema_version: string
  mapping_rules: Omit<MappingRule, 'id'>[]
}

export const CONDITION_LABELS: Record<ConditionType, string> = {
  [ConditionType.DIRECT_MAPPING]: 'Direct Mapping',
  [ConditionType.HARDCODE]: 'Hardcode',
  [ConditionType.LOOKUP_MAP]: 'Lookup Map',
  [ConditionType.GET_MAX_DATE]: 'Get Latest Date from Columns',
  [ConditionType.FIRST_AVAILABLE]: 'First Available',
  [ConditionType.FIRST_AVAILABLE_MAP]: 'First Available Map',
  [ConditionType.CONTAINS_MAP]: 'Contains Map',
  [ConditionType.MULTIPLY]: 'Multiply by Constant',
  [ConditionType.CONDITIONAL_NUMERIC]: 'Conditional numeric mapping',
  [ConditionType.CONCAT]: 'Text Join / CONCAT',
}

export const NUMERIC_COMPARISON_OPERATOR_LABELS: Record<
  NumericComparisonOperator,
  string
> = {
  [NumericComparisonOperator.EQUAL]: 'Equal',
  [NumericComparisonOperator.LESS_THAN]: 'Less than',
  [NumericComparisonOperator.LESS_THAN_OR_EQUAL]: 'Less than or equal',
  [NumericComparisonOperator.MORE_THAN]: 'More than',
  [NumericComparisonOperator.MORE_THAN_OR_EQUAL]: 'More than or equal',
}

export const NUMERIC_COMPARISON_OPERATOR_SYMBOLS: Record<
  NumericComparisonOperator,
  string
> = {
  [NumericComparisonOperator.EQUAL]: '=',
  [NumericComparisonOperator.LESS_THAN]: '<',
  [NumericComparisonOperator.LESS_THAN_OR_EQUAL]: '<=',
  [NumericComparisonOperator.MORE_THAN]: '>',
  [NumericComparisonOperator.MORE_THAN_OR_EQUAL]: '>=',
}

export const ALL_NUMERIC_COMPARISON_OPERATORS: NumericComparisonOperator[] = [
  NumericComparisonOperator.EQUAL,
  NumericComparisonOperator.LESS_THAN,
  NumericComparisonOperator.LESS_THAN_OR_EQUAL,
  NumericComparisonOperator.MORE_THAN,
  NumericComparisonOperator.MORE_THAN_OR_EQUAL,
]

export const NORMALIZE_LABELS: Record<NormalizeType, string> = {
  [NormalizeType.TRIM]: 'Trim',
  [NormalizeType.UPPERCASE]: 'Uppercase',
  [NormalizeType.LOWERCASE]: 'Lowercase',
  [NormalizeType.REMOVE_SPECIAL_CHARS]: 'Remove Special Chars',
  [NormalizeType.PAD_LEFT]: 'Pad Left',
  [NormalizeType.PAD_RIGHT]: 'Pad Right',
  [NormalizeType.DATE_FORMAT]: 'Date Format',
  [NormalizeType.MAX_LENGTH]: 'Max Length',
  [NormalizeType.SUBSTRING]: 'Substring',
  [NormalizeType.REPLACE]: 'Replace',
  [NormalizeType.SLICE_LEFT]: 'Slice Left',
  [NormalizeType.SLICE_RIGHT]: 'Slice Right',
}

export const ALL_NORMALIZE_TYPES: NormalizeType[] = [
  NormalizeType.TRIM,
  NormalizeType.UPPERCASE,
  NormalizeType.LOWERCASE,
  NormalizeType.REMOVE_SPECIAL_CHARS,
  NormalizeType.PAD_LEFT,
  NormalizeType.PAD_RIGHT,
  NormalizeType.DATE_FORMAT,
  NormalizeType.MAX_LENGTH,
  NormalizeType.SUBSTRING,
  NormalizeType.REPLACE,
  NormalizeType.SLICE_LEFT,
  NormalizeType.SLICE_RIGHT,
]

export const ALL_CONDITION_TYPES: ConditionType[] = [
  ConditionType.DIRECT_MAPPING,
  ConditionType.HARDCODE,
  ConditionType.LOOKUP_MAP,
  ConditionType.CONTAINS_MAP,
  ConditionType.GET_MAX_DATE,
  ConditionType.FIRST_AVAILABLE,
  ConditionType.FIRST_AVAILABLE_MAP,
  ConditionType.MULTIPLY,
  ConditionType.CONDITIONAL_NUMERIC,
  ConditionType.CONCAT,
]
